import { groupBy, pick } from 'lodash';

import { CustomPropertyEntityTypeEnum } from '../../../../shared/models/enum/custom-property-entity-type.enum';
import { caseDefaultPropertiesDefinition } from '../../../../shared/properties/case-default-properties.definition';
import { companyDefaultPropertiesDefinition } from '../../../../shared/properties/company-default-properties.definition';
import { individualDefaultPropertiesDefinition } from '../../../../shared/properties/individual-default-properties.definition';
import {
  Property,
  PropertyTypeEnum,
} from '../../../../shared/properties/types';

const LABEL_OVERRIDE: Record<string, string> = {
  'case.risk.level': 'Risk',
  'case.template.id': 'Template',
};

const TEMPLATE_ACTIVATION_CASE_PROPERTY_KEYS = [
  'name',
  'externalId',
  'risk.level',
  'tagIds',
];

const TEMPLATE_ACTIVATION_COMPANY_PROPERTY_KEYS = [
  'address.city',
  'address.country',
  'address.postalCode',
  'address.region',
  'address.state',
  'address.streetAddress',
  'address.streetAddress2',
  'bankingInformation',
  'classifications',
  'commercialName',
  'country',
  'employerIdentificationNumber',
  'entityLegalForm',
  'legalForm',
  'name',
  'registrationDate',
  'registrationNumber',
  'shareCapital',
  'status',
  'taxIdentificationNumber',
  'websiteUrl',
];

const TEMPLATE_ACTIVATION_INDIVIDUAL_PROPERTY_KEYS = [
  'address.city',
  'address.country',
  'address.postalCode',
  'address.region',
  'address.state',
  'address.streetAddress',
  'address.streetAddress2',
  'birthCountry',
  'birthDate',
  'email',
  'firstName',
  'isBeneficialOwner',
  'isBusinessContact',
  'isDelegator',
  'isSignatory',
  'lastName',
  'nationality',
  'phoneNumber',
  'roles',
];

export type TemplateActivationProperties = {
  case: Property<PropertyTypeEnum>[];
  company: Property<PropertyTypeEnum>[];
  individual: Property<PropertyTypeEnum>[];
};

const pickTemplateActivationProperties = (
  properties: Record<string, Property<PropertyTypeEnum>>,
  allowedKeys: string[],
): Property<PropertyTypeEnum>[] => {
  return Object.values(pick(properties, allowedKeys)).map((property) => ({
    ...property,
    label:
      LABEL_OVERRIDE[`${property.mapping.entity}.${property.mapping.key}`] ??
      property.label,
  }));
};

/**
 * Get available properties for template activation logic, organized by entity type
 * Uses the same field restrictions as controls for consistency
 */
export const getTemplateActivationProperties = (
  customProperties: Property<PropertyTypeEnum>[] = [],
): TemplateActivationProperties => {
  const groupedCustomProperties = groupBy(
    customProperties,
    (property) => property.mapping.entity,
  );

  return {
    [CustomPropertyEntityTypeEnum.case]: [
      ...pickTemplateActivationProperties(
        caseDefaultPropertiesDefinition,
        TEMPLATE_ACTIVATION_CASE_PROPERTY_KEYS,
      ),
      ...(groupedCustomProperties[CustomPropertyEntityTypeEnum.case] ?? []),
    ],
    [CustomPropertyEntityTypeEnum.company]: [
      ...pickTemplateActivationProperties(
        companyDefaultPropertiesDefinition,
        TEMPLATE_ACTIVATION_COMPANY_PROPERTY_KEYS,
      ),
      ...(groupedCustomProperties[CustomPropertyEntityTypeEnum.company] ?? []),
    ],
    [CustomPropertyEntityTypeEnum.individual]: [
      ...pickTemplateActivationProperties(
        individualDefaultPropertiesDefinition,
        TEMPLATE_ACTIVATION_INDIVIDUAL_PROPERTY_KEYS,
      ),
      ...(groupedCustomProperties[CustomPropertyEntityTypeEnum.individual] ??
        []),
    ],
  };
};
